import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * FOTOĞRAF KÜÇÜK GÖRSELİ + BÜYÜTME.
 *
 * ⚠️ NEDEN GEREKLİ: ekranlar fotoğrafı yalnız "foto 3" diye YAZIYORDU. Kanıt
 *    zincirinin (K8) tamamı sistemde duruyor ama şef göremiyor — teslimi
 *    onaylayacak kişi, onayladığı fotoğrafa bakamıyordu. Numara bir referans,
 *    kanıt değil.
 *
 * Görüntü `/api/fotolar/:no` üzerinden gelir; MinIO internete kapalı olduğu
 * için doğrudan bağlantı yok, oturum kontrolü global guard'da (kanıt zinciri
 * dışarı sızmaz).
 *
 * Fotoğraflar değişmez, sunucu `immutable` + 1 yıl önbellek gönderiyor —
 * küçük görselin tekrar tekrar indirilmesi sorun değil.
 */

export interface FotoBilgi {
  fotoNo: number;
  step?: string;
  cekilmeZamani?: string;
  /**
   * Kaydın MIME türü. Yoksa görsel varsayılır — eski çağıranlar bu alanı
   * göndermiyor ve hepsi fotoğraf.
   */
  contentType?: string | null;
  /**
   * GEÇERSİZ İŞARETLİ Mİ — yanlış yüklenen kâğıt (06.09.2026).
   *
   * ⚠️ Kayıt LİSTEDEN ÇIKMAZ, üstü çizilir. Silinemez olması K8; görünür
   *    kalması ise "ne olduğu anlaşılabilsin" gereği: bakan kişi hem yanlış
   *    belgenin durduğunu hem devre dışı bırakıldığını görmeli.
   */
  gecersiz?: boolean;
  gecersizNeden?: string | null;
}

const kaynak = (fotoNo: number) => `/api/fotolar/${fotoNo}`;

/**
 * ⚠️ PDF KANIT ZİNCİRİNDE AMA GÖRSEL DEĞİL (06.09.2026, çok sayfalı iş izni).
 *
 * `<img src>` bir PDF'i çizemez; onError dalına düşer ve kullanıcı yüklediği
 * belgeyi "kayıp" sanır. Belge kutucuğu ayrı çizilir ve yeni sekmede açılır —
 * tarayıcının kendi PDF görüntüleyicisi, sayfalar arasında gezinmeyi de
 * getirdiği için beş sayfalık izin için doğru araç.
 */
export const belgeMi = (foto: FotoBilgi): boolean => foto.contentType === 'application/pdf';

/**
 * Tek küçük görsel. Tıklanınca büyür.
 *
 * ⚠️ Faz 4 öncesi test kayıtlarının dosyası YOK (`beklemede:` anahtarı) ve
 *    sunucu 404 dönüyor. Kırık resim ikonu göstermek yerine numarayı yazıyoruz:
 *    kayıt gerçek, eksik olan dosya.
 */
export function FotoKucuk({
  foto,
  boyut = 56,
  onBuyut,
}: {
  foto: FotoBilgi;
  boyut?: number;
  onBuyut: (fotoNo: number) => void;
}) {
  /** Geçersiz kayıt sönük ve çapraz şeritli — bakışta ayırt edilsin. */
  const gecersizSinif = foto.gecersiz ? 'opacity-45 grayscale' : '';
  const [hata, setHata] = useState(false);

  /*
   * BELGE (PDF) — büyütme katmanına girmez, yeni sekmede açılır. Zoom katmanı
   * tek kareyi ölçekliyor; çok sayfalı bir belgede ikinci sayfaya geçmenin yolu
   * yok, tarayıcının PDF görüntüleyicisinde ise var.
   */
  if (belgeMi(foto)) {
    return (
      <a
        href={kaynak(foto.fotoNo)}
        target="_blank"
        rel="noreferrer"
        title={`Belge ${foto.fotoNo} (PDF) — yeni sekmede açılır`}
        className={`group relative flex flex-col items-center justify-center gap-1 rounded-(--radius-mod) border border-line-2 bg-ground text-wait hover:border-navy hover:text-navy ${
          foto.gecersiz ? 'opacity-45' : ''
        }`}
        style={{ width: boyut, height: boyut }}
      >
        <span className="text-[10px] font-semibold tracking-wide">PDF</span>
        <span className="num text-[10px] leading-tight">{foto.fotoNo}</span>
        {foto.gecersiz && (
          <span className="absolute inset-x-0 top-0 bg-crit/85 px-1 text-[9px] leading-tight font-medium text-white">
            geçersiz
          </span>
        )}
      </a>
    );
  }

  if (hata) {
    return (
      <span
        title="Bu kaydın dosyası yok (Faz 4 öncesi test verisi)"
        className="num inline-flex items-center justify-center rounded-(--radius-mod) border border-dashed border-line-2 text-[11px] text-wait"
        style={{ width: boyut, height: boyut }}
      >
        {foto.fotoNo}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onBuyut(foto.fotoNo)}
      title={
        foto.gecersiz
          ? `Foto ${foto.fotoNo} — GEÇERSİZ${foto.gecersizNeden ? `: ${foto.gecersizNeden}` : ''}`
          : `Foto ${foto.fotoNo} — büyütmek için tıklayın`
      }
      className="group relative overflow-hidden rounded-(--radius-mod) border border-line-2 hover:border-navy"
      style={{ width: boyut, height: boyut }}
    >
      <img
        src={kaynak(foto.fotoNo)}
        alt={`Foto ${foto.fotoNo}`}
        loading="lazy"
        onError={() => setHata(true)}
        className={`h-full w-full object-cover ${gecersizSinif}`}
      />
      <span className="num absolute right-0 bottom-0 bg-black/55 px-1 text-[10px] leading-tight text-white">
        {foto.fotoNo}
      </span>
      {foto.gecersiz && (
        <span className="absolute inset-x-0 top-0 bg-crit/85 px-1 text-[9px] leading-tight font-medium text-white">
          geçersiz
        </span>
      )}
    </button>
  );
}

/**
 * Bir adımın fotoğrafları — küçük görsel şeridi + ortak büyütme katmanı.
 * Hiç fotoğraf yoksa hiçbir şey basmaz (çağıran taraf kendi boş metnini yazar).
 */
export function FotoSerit({
  fotolar,
  boyut = 56,
  onGecersiz,
  eylem = 'yanlış mı?',
}: {
  fotolar: FotoBilgi[];
  boyut?: number;
  /**
   * "Bu yanlış fotoğraf" akışı. Verilmezse eylem HİÇ ÇİZİLMEZ — yetkisi
   * olmayan (usta) ya da kapanmış işte kimse bu düğmeyi görmez.
   *
   * ⚠️ GEREKÇE ZORUNLU ve burada toplanır; sunucu da bağımsız olarak arar.
   */
  onGecersiz?: (fotoNo: number, gerekce: string) => Promise<void> | void;
  /**
   * Küçük görselin altındaki eylemin adı.
   *
   * ⚠️ "DEĞİŞTİR" YALNIZ DOĞRUSU YÜKLENEBİLEN ADIMDA (09.10.2026, Akçansa).
   *    İş emri ve izin kâğıdında yükleme yolu her zaman açık; orada kullanıcının
   *    asıl niyeti "yanlış kâğıdı değiştirmek" ve "yanlış mı?" bunu söylemiyordu
   *    (06.09'dan 08.10'a bir kez kullanıldı). Keşif fotoğrafı gibi sonradan
   *    yükleme yolu olmayan adımda "değiştir" yanlış söz verirdi; orada varsayılan kalır.
   */
  eylem?: string;
}) {
  const [buyuk, setBuyuk] = useState<number | null>(null);
  /** Gerekçe kutusu açık olan foto — aynı anda yalnız biri. */
  const [isaretlenen, setIsaretlenen] = useState<number | null>(null);
  const [gerekce, setGerekce] = useState('');
  const [mesgul, setMesgul] = useState(false);
  if (fotolar.length === 0) return null;

  const sirali = [...fotolar].sort((a, b) => a.fotoNo - b.fotoNo);
  /*
   * Ok tuşlarıyla gezilen liste YALNIZ GÖRSELLERDEN oluşur. PDF'i araya
   * katsaydık, şef teslim onaylarken sağ oka basınca boş bir katman açılırdı.
   */
  const gezilebilir = sirali.filter((f) => !belgeMi(f)).map((f) => f.fotoNo);

  async function isaretle(fotoNo: number) {
    if (gerekce.trim().length < 3 || !onGecersiz) return;
    setMesgul(true);
    try {
      await onGecersiz(fotoNo, gerekce.trim());
      setIsaretlenen(null);
      setGerekce('');
    } finally {
      setMesgul(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap justify-center gap-1.5">
        {sirali.map((f) => (
          <div key={f.fotoNo} className="flex flex-col items-center gap-0.5">
            <FotoKucuk foto={f} boyut={boyut} onBuyut={setBuyuk} />
            {/*
              ⚠️ EYLEM KÜÇÜK VE İKİNCİL — kanıtı devre dışı bırakmak sık yapılan
                 bir iş değil, kazara basılmamalı. Aynı gerekçe K5 override'ın
                 düğme değil link olmasının gerekçesi.
            */}
            {onGecersiz && !f.gecersiz && (
              <button
                type="button"
                onClick={() => {
                  setIsaretlenen(isaretlenen === f.fotoNo ? null : f.fotoNo);
                  setGerekce('');
                }}
                className="text-[10px] text-muted underline hover:text-crit"
              >
                {eylem}
              </button>
            )}
          </div>
        ))}
      </div>

      {/*
        GEREKÇE KUTUSU. "Bu bir düzeltmedir, gerekçesi iz kaydına yazılır"
        mesajı burada veriliyor — kullanıcı silmediğini, işaretlediğini bilsin.
      */}
      {isaretlenen !== null && (
        <div className="mt-2 rounded-(--radius-mod) border border-crit-line bg-crit-bg p-3">
          <p className="mb-2 text-xs text-ink-2">
            Foto <span className="num">{isaretlenen}</span> geçersiz işaretlenecek. Fotoğraf{' '}
            <strong>silinmez</strong> (kanıt zinciri): müşteriye giden rapor ve maillerde
            görünmez, iş emri/izin kapısında sayılmaz, iz kaydında durur. Doğrusunu ayrıca
            yükleyin.
          </p>
          <input
            autoFocus
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            placeholder="Gerekçe (ör. yanlış iş emri kâğıdı yüklendi)"
            className="w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={mesgul || gerekce.trim().length < 3}
              onClick={() => void isaretle(isaretlenen)}
              className="rounded-(--radius-mod) bg-crit px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            >
              {mesgul ? 'İşaretleniyor…' : 'Geçersiz işaretle'}
            </button>
            <button
              type="button"
              onClick={() => setIsaretlenen(null)}
              className="rounded-(--radius-mod) border border-line-2 px-3 py-1.5 text-xs text-wait"
            >
              Vazgeç
            </button>
          </div>
        </div>
      )}
      {buyuk !== null && (
        <FotoBuyut
          fotoNo={buyuk}
          hepsi={gezilebilir}
          onKapat={() => setBuyuk(null)}
          onDegis={setBuyuk}
        />
      )}
    </>
  );
}

/** Yakınlaştırma sınırları. Üst sınır 6× — 2000px'lik bir kanıt fotoğrafında
 *  yeşil kart üzerindeki el yazısını okumaya yetiyor, ötesi piksel bulanıklığı. */
const OLCEK_MIN = 1;
const OLCEK_MAX = 6;
const kis = (n: number) => Math.min(OLCEK_MAX, Math.max(OLCEK_MIN, n));

/**
 * BÜYÜTME KATMANI — yakınlaştırmalı.
 *
 * Escape ile kapanır, ok tuşlarıyla gezilir — şef teslim onaylarken üç
 * fotoğrafa arka arkaya bakıyor; her seferinde kapatıp açmak gereksiz.
 *
 * ⚠️ ZOOM NEDEN GEREKLİ (kullanıcı isteği 05.08.2026): büyütme ekrana sığdırılmış
 *    tek bir kareydi. Kanıt fotoğrafları (yeşil kart, imzalı izin kâğıdı, iş emri)
 *    ÜZERİNDE YAZI TAŞIR; onaylayan kişi o yazıyı okuyamıyordu. Kanıtı görmek
 *    yetmez, OKUNABİLİR olmalı — K8'in pratikteki karşılığı bu.
 *
 * Üç giriş yolu da desteklenir, çünkü ekran hem masaüstünde hem sahada açılıyor:
 * tekerlek (imleç neredeyse oraya), çift tık/dokunuş, iki parmak (pinch).
 */
export function FotoBuyut({
  fotoNo,
  hepsi,
  onKapat,
  onDegis,
  etiket,
  ek,
}: {
  fotoNo: number;
  hepsi: number[];
  onKapat: () => void;
  onDegis: (no: number) => void;
  /**
   * Çubuktaki ad. Verilmezse "Foto 271".
   *
   * ⚠️ Katman fotoğrafın hangi işe ait olduğunu BİLMEZ ve bilmemeli. Ama arşiv
   *    aramasında kullanıcının okuduğu şey foto numarası değil ONAY numarası
   *    (istek 11.08.2026); adı çağıran koyabilsin diye bu kapı var.
   */
  etiket?: string;
  /**
   * Kontrollerin ALTINA konan serbest içerik — arşiv aramasında fotoğrafın
   * hangi işe ait olduğu ve "İşe git" düğmesi buradan geliyor.
   *
   * ⚠️ Katmanın kendisi bunu BİLMEZ: foto gösterici bir işin ayrıntılarını
   *    tanımak zorunda değil. Çağıran ne göstereceğine kendi karar verir.
   */
  ek?: React.ReactNode;
}) {
  const i = hepsi.indexOf(fotoNo);
  const cok = hepsi.length > 1;

  const [olcek, setOlcek] = useState(1);
  const [kaydir, setKaydir] = useState({ x: 0, y: 0 });
  /** Bağlam paneli (künye + "İşe git") — kapalı başlar, bkz. aşağıdaki gerekçe. */
  const [bilgiAcik, setBilgiAcik] = useState(false);
  const sarmal = useRef<HTMLDivElement>(null);
  const resim = useRef<HTMLImageElement>(null);
  /** Aktif işaretçiler — tek parmak sürükler, iki parmak yakınlaştırır. */
  const isaretciler = useRef(new Map<number, { x: number; y: number }>());
  /** İki parmak arası son mesafe (pinch ölçeği bundan çıkar). */
  const sonMesafe = useRef<number | null>(null);
  /*
   * ⚠️ SÜRÜKLEME İLE TIKLAMA AYRILMALI. Katman tıklanınca kapanıyor; kaydırmak
   *    için parmağını sürükleyen kullanıcı, parmağını kaldırdığında fotoğrafın
   *    KAPANDIĞINI görüyordu. Bu bayrak "bu bir sürüklemeydi, tıklama değil" der.
   */
  const suruklendi = useRef(false);

  const sifirla = useCallback(() => {
    setOlcek(1);
    setKaydir({ x: 0, y: 0 });
  }, []);

  // Fotoğraf değişince yakınlaştırma sıfırlanır — bir öncekinin köşesine
  // kilitlenmiş görünümle yeni fotoğrafa bakmak yön kaybettiriyor.
  useEffect(() => {
    sifirla();
  }, [fotoNo, sifirla]);

  /*
   * Ölçek her değiştiğinde kaydırmayı yeniden sınırla. Uzaklaşırken görselin
   * kenarında duran kullanıcı, sınır güncellenmezse boşluğa bakıyordu.
   * Tek yerde: tekerlek, pinch, çift tık ve +/− düğmeleri hepsi buradan geçer.
   */
  useEffect(() => {
    setKaydir((k) => sinirla(k, olcek));
    // `bilgiAcik` de burada: panel açılıp kapanınca görsel alanının yüksekliği
    // değişiyor, eski sınırlar geçersiz kalıyor ve fotoğraf alanın dışında asılı
    // kalabiliyordu.
  }, [olcek, bilgiAcik]);

  useEffect(() => {
    const tus = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Yakınlaştırılmışsa ilk Escape zoom'u sıfırlar, ikincisi kapatır —
        // kullanıcı incelemeyi bitirirken pencereyi kazara kapatmasın.
        if (olcek > 1) return sifirla();
        return onKapat();
      }
      if (e.key === '+' || e.key === '=') return setOlcek((o) => kis(o + 0.5));
      if (e.key === '-') return setOlcek((o) => kis(o - 0.5));
      if (e.key === '0') return sifirla();
      // Yakınlaştırılmışken oklar GEZİNMEZ: o sırada oklarla kaydırmak beklenir.
      if (olcek > 1) {
        const adim = 60;
        const kaydirVe = (dx: number, dy: number) =>
          setKaydir((k) => sinirla({ x: k.x + dx, y: k.y + dy }, olcek));
        if (e.key === 'ArrowRight') return kaydirVe(-adim, 0);
        if (e.key === 'ArrowLeft') return kaydirVe(adim, 0);
        if (e.key === 'ArrowUp') return kaydirVe(0, adim);
        if (e.key === 'ArrowDown') return kaydirVe(0, -adim);
        return;
      }
      if (cok && e.key === 'ArrowRight') onDegis(hepsi[(i + 1) % hepsi.length]!);
      if (cok && e.key === 'ArrowLeft') onDegis(hepsi[(i - 1 + hepsi.length) % hepsi.length]!);
    };
    window.addEventListener('keydown', tus);
    // Arkadaki sayfa kaymasın — büyük fotoğrafta parmakla kaydırırken
    // sayfanın altta kayması yön kaybettiriyor.
    const eskiTasma = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', tus);
      document.body.style.overflow = eskiTasma;
    };
  }, [i, cok, hepsi, onKapat, onDegis, olcek, sifirla]);

  /**
   * İMLECİN ALTINDAKİ NOKTAYI SABİT TUTARAK yakınlaştır.
   *
   * ⚠️ Merkezden yakınlaştırmak, kullanıcının baktığı yeri ekran dışına itiyordu:
   *    yeşil kartın köşesindeki numaraya yaklaşmak isteyen kişi, her adımda
   *    hedefi kaybedip yeniden kaydırmak zorunda kalıyordu.
   */
  const noktadanYakinlastir = useCallback(
    (yeni: number, noktaX: number, noktaY: number) => {
      const kutu = sarmal.current?.getBoundingClientRect();
      if (!kutu) return setOlcek(kis(yeni));
      const merkezX = kutu.left + kutu.width / 2;
      const merkezY = kutu.top + kutu.height / 2;
      setOlcek((eski) => {
        const hedef = kis(yeni);
        const oran = hedef / eski;
        setKaydir((k) => ({
          x: noktaX - merkezX - (noktaX - merkezX - k.x) * oran,
          y: noktaY - merkezY - (noktaY - merkezY - k.y) * oran,
        }));
        // 1×'e dönüldüyse kaydırma da sıfırlanır; aksi hâlde fotoğraf ekranın
        // kenarında asılı kalıyordu.
        if (hedef === 1) setKaydir({ x: 0, y: 0 });
        return hedef;
      });
    },
    [],
  );

  /*
   * ⚠️ `wheel` DİNLEYİCİSİ ELLE BAĞLANIYOR, JSX `onWheel` ile değil. React'in
   *    wheel dinleyicisi pasiftir ve `preventDefault` çağrılamaz; sayfa arkada
   *    kayardı. `{ passive: false }` bunun tek yolu.
   */
  useEffect(() => {
    const el = sarmal.current;
    if (!el) return;
    const tekerlek = (e: WheelEvent) => {
      e.preventDefault();
      noktadanYakinlastir(olcek * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
    };
    el.addEventListener('wheel', tekerlek, { passive: false });
    return () => el.removeEventListener('wheel', tekerlek);
  }, [olcek, noktadanYakinlastir]);

  const mesafe = () => {
    const [a, b] = [...isaretciler.current.values()];
    if (!a || !b) return null;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  function isaretciBasla(e: React.PointerEvent) {
    isaretciler.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    suruklendi.current = false;
    if (isaretciler.current.size === 2) sonMesafe.current = mesafe();
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function isaretciOynat(e: React.PointerEvent) {
    const onceki = isaretciler.current.get(e.pointerId);
    if (!onceki) return;
    isaretciler.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // İKİ PARMAK — pinch.
    if (isaretciler.current.size === 2) {
      const yeni = mesafe();
      if (yeni != null && sonMesafe.current != null && sonMesafe.current > 0) {
        const [a, b] = [...isaretciler.current.values()];
        suruklendi.current = true;
        noktadanYakinlastir(
          olcek * (yeni / sonMesafe.current),
          (a!.x + b!.x) / 2,
          (a!.y + b!.y) / 2,
        );
      }
      sonMesafe.current = yeni;
      return;
    }

    // TEK PARMAK — yalnız yakınlaştırılmışken kaydırır. 1×'te sürükleme
    // kaydırma değil, "kapat" niyetidir; fotoğrafı yerinden oynatmıyoruz.
    if (olcek <= 1) return;
    const dx = e.clientX - onceki.x;
    const dy = e.clientY - onceki.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) suruklendi.current = true;
    setKaydir((k) => sinirla({ x: k.x + dx, y: k.y + dy }, olcek));
  }

  /**
   * KAYDIRMAYI GÖRSELİN KENDİ SINIRINDA TUT.
   *
   * ⚠️ Sınırsız kaydırma, büyütülmüş fotoğrafı ekran dışına itip kullanıcıyı
   *    bomboş siyah bir ekranla baş başa bırakıyordu — o noktada tek çare
   *    sıfırlamayı bulmak. Görselin kenarı en fazla ekranın kenarına gelebilir.
   */
  function sinirla(k: { x: number; y: number }, o: number) {
    const el = resim.current;
    const kutu = sarmal.current;
    if (!el || !kutu) return k;
    const enFazlaX = Math.max(0, (el.offsetWidth * o - kutu.clientWidth) / 2);
    const enFazlaY = Math.max(0, (el.offsetHeight * o - kutu.clientHeight) / 2);
    return {
      x: Math.min(enFazlaX, Math.max(-enFazlaX, k.x)),
      y: Math.min(enFazlaY, Math.max(-enFazlaY, k.y)),
    };
  }

  function isaretciBitir(e: React.PointerEvent) {
    isaretciler.current.delete(e.pointerId);
    if (isaretciler.current.size < 2) sonMesafe.current = null;
  }

  const dugme =
    'rounded-(--radius-mod) bg-white/15 px-3 py-1.5 hover:bg-white/25 disabled:opacity-35';

  return (
    /*
      ⚠️ KATMAN ARTIK İKİ SATIRLIK BİR SÜTUN, üst üste binen katmanlar değil
         (kullanıcı bildirimi 11.08.2026: "toolbar görseli kapatıyor").

      Önce kontroller `absolute bottom-0` ile fotoğrafın ÜSTÜNDE yüzüyordu.
      Yüzen çubuk, ekrana sığdırılmış (1×) fotoğrafın alt şeridini de,
      yakınlaştırılmış fotoğrafın o anda bakılan bölgesini de kapatıyordu —
      hem de tam kanıt fotoğraflarının imza/tarih taşıdığı alt kenarını.

      Şimdi görsel alanı `flex-1`, çubuk `shrink-0`: ikisi yer PAYLAŞIYOR,
      çakışmıyor. Fotoğraf hiçbir yakınlaştırma oranında çubuğun altına
      giremez, çünkü kırpan kutu artık çubuğun bittiği yerde bitiyor.
    */
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/80">
      {/*
        YAKINLAŞTIRMA YÜZEYİ = KONTROLLERİN ÜSTÜNDE KALAN TÜM ALAN.

        ⚠️ Bir zamanlar bu sarmalayıcı görselin KENDİ kutusu kadardı
           (`max-h-[78vh]` + `overflow-hidden`) ve fotoğraf küçük bir çerçeveye
           hapis kalıyordu (kullanıcı bildirimi 05.08.2026). O kısıt kalktı;
           yüzey hâlâ ekranın alabildiği en büyük alan — yalnız çubuğun işgal
           ettiği şerit dışarıda.

        `touch-none` olmadan tarayıcı iki parmağı SAYFA yakınlaştırması sanıyor
        ve pointer olayları hiç gelmiyor.
      */}
      <div
        ref={sarmal}
        className="relative flex min-h-0 flex-1 touch-none items-center justify-center overflow-hidden"
        onPointerDown={isaretciBasla}
        onPointerMove={isaretciOynat}
        onPointerUp={isaretciBitir}
        onPointerCancel={isaretciBitir}
        onClick={(e) => {
          // Sürükleme bitişi TIKLAMA SAYILMAZ — kaydırırken parmağını kaldıran
          // kullanıcı fotoğrafın kapandığını görüyordu.
          if (suruklendi.current) {
            suruklendi.current = false;
            return;
          }
          // Yalnız BOŞLUĞA tıklamak kapatır; görselin üstü (target !== surface) kapatmaz.
          if (e.target === e.currentTarget) onKapat();
        }}
        onDoubleClick={(e) => {
          if (olcek > 1) sifirla();
          else noktadanYakinlastir(2.5, e.clientX, e.clientY);
        }}
      >
        <img
          ref={resim}
          src={kaynak(fotoNo)}
          alt={`Foto ${fotoNo}`}
          draggable={false}
          style={{
            transform: `translate(${kaydir.x}px, ${kaydir.y}px) scale(${olcek})`,
            // Sürüklerken geçiş animasyonu parmağın arkasında sürükleniyor;
            // yalnız düğme/çift tık ile ölçek değişiminde yumuşatıyoruz.
            transition: suruklendi.current ? 'none' : 'transform 120ms ease-out',
            cursor: olcek > 1 ? 'grab' : 'zoom-in',
          }}
          /*
            Sınır artık SATIRIN kendisi (`max-h-full`), sabit bir viewport oranı
            değil: 1×'te fotoğraf kullanılabilir alanı tam doldurur, büyütülünce
            `object-contain` sınırı aşılır ve alanı taşar — ama satırın dışına,
            yani çubuğun altına, asla geçmez.
          */
          className="max-h-full max-w-full object-contain select-none"
        />
      </div>

      {/*
        KONTROL ŞERİDİ — akışta, yüzen değil. Kapladığı yer görselden düşülür;
        karşılığında hiçbir şeyi örtmez.
      */}
      <div className="z-10 flex shrink-0 flex-col items-center gap-2 bg-black/40 px-3 py-2 backdrop-blur-sm">
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex flex-wrap items-center justify-center gap-2 text-sm text-white"
        >
          {cok && (
            <button
              type="button"
              onClick={() => onDegis(hepsi[(i - 1 + hepsi.length) % hepsi.length]!)}
              className={dugme}
            >
              ‹ Önceki
            </button>
          )}
          <span className="num">
            {etiket ?? `Foto ${fotoNo}`}
            {cok && <span className="text-white/60"> · {i + 1}/{hepsi.length}</span>}
          </span>
          {cok && (
            <button
              type="button"
              onClick={() => onDegis(hepsi[(i + 1) % hepsi.length]!)}
              className={dugme}
            >
              Sonraki ›
            </button>
          )}

          <span className="mx-1 h-5 w-px bg-white/25" />

          <button
            type="button"
            title="Uzaklaştır (−)"
            aria-label="Uzaklaştır"
            disabled={olcek <= OLCEK_MIN}
            onClick={() => setOlcek((o) => kis(o - 0.5))}
            className={dugme}
          >
            −
          </button>
          {/* Oran GÖRÜNÜR: kullanıcı neye baktığını ve sıfırlamanın ne yapacağını bilir. */}
          <button
            type="button"
            title="Sıfırla (0)"
            onClick={sifirla}
            className={`${dugme} num min-w-14`}
          >
            {olcek.toFixed(1)}×
          </button>
          <button
            type="button"
            title="Yakınlaştır (+)"
            aria-label="Yakınlaştır"
            disabled={olcek >= OLCEK_MAX}
            onClick={() => setOlcek((o) => kis(o + 0.5))}
            className={dugme}
          >
            +
          </button>

          {/*
            BAĞLAM PANELİ KATLANIR ve KAPALI BAŞLAR.

            ⚠️ Açık başlarsa (eski hâli) dört satırlık künye + "İşe git" düğmesi
               ekranın alt üçte birini kalıcı olarak alır. Bu ekranda öncelik
               KANIT: kullanıcı fotoğrafı görmek için geldi, işe gitmek isteğe
               bağlı bir sonraki adım (bkz. Arşiv ekranı başlığı). Bağlam bir
               tık uzakta duruyor, görselin yerini almıyor.
          */}
          {ek && (
            <>
              <span className="mx-1 h-5 w-px bg-white/25" />
              <button
                type="button"
                onClick={() => setBilgiAcik((v) => !v)}
                aria-expanded={bilgiAcik}
                title="Bu fotoğraf hangi işe ait?"
                className={dugme}
              >
                Bilgi {bilgiAcik ? '▴' : '▾'}
              </button>
            </>
          )}

          <span className="mx-1 h-5 w-px bg-white/25" />

          <button type="button" onClick={onKapat} className={dugme}>
            Kapat
          </button>
        </div>

        {ek && bilgiAcik && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-(--radius-mod) bg-white/10 p-3 text-sm text-white"
          >
            {ek}
          </div>
        )}

        {/* İpucu yalnız 1×'te: kullanıcı yakınlaştırdıysa nasıl yapıldığını
            zaten öğrenmiştir ve o satır artık yalnız yer kaplar. */}
        {olcek === 1 && (
          <p className="hidden text-center text-[11px] text-white/40 sm:block">
            Çift dokun ya da iki parmakla yakınlaştır · tekerlek · sürükleyerek gez
          </p>
        )}
      </div>
    </div>
  );
}
